import { createContext } from 'react';
import { ITrackingEventConfig } from '../hooks/useGoogleAnalytics';
import { ConsentmanagerDeConfig } from '../thirdParty/consent/adapters/ConsentmanagerDeAdapter';
import { UsercentricsConfig } from '../thirdParty/consent/adapters/UsercentricsAdapter';
import { ConsentManagerVendor } from '../thirdParty/consent/consent.types';
import { ILink } from './loginContext';

export interface IRegistrationFields {
    birthDate: boolean;
    city: boolean;
    country: boolean;
    email: boolean;
    firstName: boolean;
    gender: boolean;
    lastName: boolean;
    phone: boolean;
    street: boolean;
    zip: boolean;
    insuranceNr: boolean;
    mobilePhone: boolean;
    academicTitle: boolean;
    insuranceName: boolean;
    language: boolean;
    privacyTerms: boolean;
}

export interface IAppointmentSummary {
    behandler: boolean;
    fach: boolean;
    pflege: boolean;
    raum: boolean;
    standort: boolean;
}

export interface IAppointmentGeneral {
    skipBehandlersuche?: boolean;
}

export interface ICustomTexts {
    CredentialsLost: string;
}

export interface IImpressum {
    impressumLink: string;
    datenschutzLink: string;
    barrierefreiheitLink: string;
}

export interface IAnmeldeContexts {
    code: string;
    name: string;
}

export interface IConsentManagement {
    enabled: boolean;
    vendor: ConsentManagerVendor;
    vendorConfig?: UsercentricsConfig | ConsentmanagerDeConfig;
}

export type InitialAppPropsType = {
    allowAccountAfterAppointment: boolean;
    allowAccountCreateDirect: boolean;
    allowAccountInAnmeldeworkflow: boolean;
    allowAppointments: boolean;
    allowAppointmentsEinweiser: boolean;
    allowAppointmentsWithoutLogin: boolean;
    activate2FAForVAN: boolean;
    allowChat: boolean;
    allowContactPerson: boolean;
    allowDirectAppointments: boolean;
    allowDirectAppointmentsWithoutLogin: boolean;
    allowLogin: boolean;
    allowVideo: boolean;
    allowVideoWithoutLogin: boolean;
    anmeldeContexts: Array<IAnmeldeContexts>;
    Appointment?: IAppointmentGeneral;
    AppointmentSummary: IAppointmentSummary;
    appPropsSet: boolean;
    availableLanguages: string;
    brandigType: string;
    brandingBytes: string;
    cbxShowAboutbutton: boolean;
    cbxShowPatAddrEinweiser: boolean;
    cbxShowPatOrganization: boolean;
    cbxShowPIDEinweiser: boolean;
    consentManagerConfig: IConsentManagement;
    countDaysToVisualize?: number;
    customTexts: ICustomTexts;
    disableTerms: boolean;
    extendedPhysicians: boolean;
    extraLinks: Array<ILink>;
    fullscreenMode: boolean;
    googleAnalyticsID?: string;
    googleTagManagerID?: string;
    impressum: IImpressum;
    jitsiDomain: string;
    jitsiOptions: any;
    mailFromAddress?: string;
    possibleCountries: string;
    RegistrationMandFields: IRegistrationFields;
    RegistrationReadOnlyFields: IRegistrationFields;
    RegistrationVisibleFields: IRegistrationFields;
    showMobileQR: boolean;
    smtpUser?: string;
    tileTexts?: any;
    trackingConfig: ITrackingEventConfig[];
};

export const InitialAppPropsState = {
    appPropsSet: false,
    Appointment: {
        skipBehandlersuche: false,
    },
    AppointmentSummary: {
        behandler: true,
        fach: true,
        pflege: true,
        raum: true,
        standort: true,
    },
    RegistrationMandFields: {
        birthDate: false,
        city: false,
        country: false,
        email: true,
        firstName: true,
        gender: false,
        lastName: true,
        phone: false,
        street: false,
        zip: false,
        insuranceNr: false,
        mobilePhone: false,
        academicTitle: false,
        insuranceName: false,
        language: false,
        privacyTerms: false,
    },
    RegistrationVisibleFields: {
        birthDate: true,
        city: true,
        country: true,
        email: true,
        firstName: true,
        gender: true,
        lastName: true,
        phone: true,
        street: true,
        zip: true,
        insuranceNr: false,
        mobilePhone: true,
        academicTitle: true,
        insuranceName: true,
        language: true,
        privacyTerms: false,
    },
    RegistrationReadOnlyFields: {
        birthDate: false,
        city: false,
        country: false,
        email: false,
        firstName: false,
        gender: false,
        lastName: false,
        phone: false,
        street: false,
        zip: false,
        insuranceNr: false,
        mobilePhone: false,
        academicTitle: false,
        insuranceName: false,
        language: false,
        privacyTerms: false,
    },
    allowAccountAfterAppointment: false,
    allowAccountCreateDirect: false,
    allowAppointments: false,
    allowAppointmentsEinweiser: false,
    allowAppointmentsWithoutLogin: false,
    activate2FAForVAN: false,
    allowDirectAppointments: false,
    allowDirectAppointmentsWithoutLogin: false,
    allowLogin: false,
    allowVideo: false,
    allowVideoWithoutLogin: false,
    allowAccountInAnmeldeworkflow: true,
    allowContactPerson: false,
    brandingBytes: '',
    brandigType: '',
    extraLinks: [],
    customTexts: {
        CredentialsLost: '',
    },
    showMobileQR: true,
    jitsiDomain: '',
    jitsiOptions: null,
    impressum: {
        impressumLink: '',
        datenschutzLink: '',
        barrierefreiheitLink: '',
    },
    anmeldeContexts: [],
    countDaysToVisualize: 0,
    extendedPhysicians: false,
    allowChat: false,
    fullscreenMode: false,
    googleAnalyticsID: '',
    googleTagManagerID: '',
    trackingConfig: [],
    consentManagerConfig: {
        enabled: false,
        vendor: 'none' as const,
    },
    tileTexts: {},
    smtpUser: '',
    mailFromAddress: '',
    possibleCountries: '',
    availableLanguages: 'de',
    disableTerms: false,
    cbxShowAboutbutton: true,
    cbxShowPIDEinweiser: false,
    cbxShowPatAddrEinweiser: false,
    cbxShowPatOrganization: true,
};

const AppPropsContext = createContext<{
    apstate: InitialAppPropsType;
    apdispatch: React.Dispatch<any>;
}>({
    apstate: InitialAppPropsState,
    apdispatch: () => null,
});

export default AppPropsContext;
